import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Agentation } from "agentation";
import {
  ErrorBoundary,
  AvatarErrorBoundary,
  NotificationProvider
} from "@/components/ErrorBoundary";
import { AuthGuard } from "@/components/AuthGuard";
import { IndustryRouteGuard } from "@/components/IndustryRouteGuard";
import TransactionDebugger from "@/utils/transactionDebugger";
import AdminGuard from "@/components/admin/AdminGuard";
import { CompanyType } from "@/types/company";
import { ThemeUpdateNotice } from "@/components/notifications";
import Index from "./pages/Index";
import Login from "./pages/Login";
import DashboardPage from "./pages/DashboardPage";
import Transactions from "./pages/Transactions";
import NotFound from "./pages/NotFound";
import PricingPage from "./pages/PricingPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import RecurringPage from "./pages/RecurringPage";
import InstallmentTransactionsPage from "./pages/InstallmentsPage";
import SettingsLayout from "./pages/settings/SettingsLayout";
import ProfileSettings from "./pages/settings/ProfileSettings";
import CompanySettings from "./pages/settings/CompanySettings";
import NotificationsSettings from "./pages/settings/NotificationsSettings";
import SecuritySettings from "./pages/settings/SecuritySettings";
import DeleteAccount from "./pages/settings/DeleteAccount";
import ReportsPage from "./pages/ReportsPage";
import CalendarPage from "./pages/CalendarPage";
import ErrorTestPage from "./pages/ErrorTestPage";
import { Reconciliation } from "./pages/Reconciliation";
import { CashFlow } from "./pages/CashFlow";
import { Overdue } from "./pages/Overdue";
import { Accrual } from "./pages/Accrual";
import CostCentersPage from "./pages/settings/cadastros/CostCentersPage";
import CategoriesPage from "./pages/settings/cadastros/CategoriesPage";
import PaymentAccountsPage from "./pages/settings/cadastros/PaymentAccountsPage";
import ClientsPage from "./pages/settings/cadastros/ClientsPage";
import SuppliersPage from "./pages/settings/cadastros/SuppliersPage";
import CompanyUsersPage from "./pages/settings/CompanyUsersPage";
import FeedbackDashboardPage from "./pages/admin/feedbacks/FeedbackDashboardPage";
// Services pages
import BudgetsLayout from "./pages/services/budgets/BudgetsLayout";
import BudgetsListPage from "./pages/services/budgets/BudgetsListPage";
import BudgetFormPage from "./pages/services/budgets/BudgetFormPage";
import BudgetViewPage from "./pages/services/budgets/BudgetViewPage";
import BudgetDuplicatePage from "./pages/services/budgets/BudgetDuplicatePage";
import BudgetAnalyticsPage from "./pages/services/budgets/BudgetAnalyticsPage";
import BudgetReportsPage from "./pages/services/budgets/BudgetReportsPage";
import BudgetConversionPage from "./pages/services/budgets/BudgetConversionPage";
import SalesPage from "./pages/services/sales/SalesPage";
import { SalesListPage } from "./pages/services/sales";
import { SalesModalRoute } from "./components/modals/services/SalesModalRoute";
import ServicesContractsPage from "./pages/services/ContractsPage";
import WorkOrdersPage from "./pages/services/WorkOrdersPage";
import InstallmentsPage from "./pages/services/InstallmentsPage";
import ServicesInvoicesPage from "./pages/services/InvoicesPage";
import SerasaPage from "./pages/services/SerasaPage";
import DueRemindersPage from "./pages/services/DueRemindersPage";
import ServicesClientsPage from "./pages/services/cadastros/ClientsPage";
import ServicesServicesPage from "./pages/services/cadastros/ServicesPage";
import CarriersPage from "./pages/services/cadastros/CarriersPage";
import InvoicesSettingsPage from "./pages/services/settings/InvoicesSettingsPage";
import EmailTemplatePage from "./pages/services/settings/EmailTemplatePage";
import InvoiceSeriesPage from "./pages/services/settings/InvoiceSeriesPage";
import { AdminLayout } from "@/components/admin/AdminLayout";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import UsersPage from "./pages/admin/UsersPage";
import WhatsAppPage from "./pages/admin/WhatsAppPage";
import ColorPalettesPage from "./pages/admin/ColorPalettesPage";
import FeatureFlagsPage from "./pages/admin/FeatureFlagsPage";
import PaymentsPage from "./pages/admin/PaymentsPage";
import SubscriptionsPage from "./pages/admin/SubscriptionsPage";
import ProjectDashboardPage from "./pages/projects/ProjectDashboardPage";
import ContractsPage from "./pages/projects/ContractsPage";
import ProjectsListPage from "./pages/projects/ProjectsListPage";
import AddExpensePage from "./pages/projects/AddExpensePage";
import ProfitabilityPage from "./pages/projects/ProfitabilityPage";
import ConstructionDashboardPage from "./pages/construction/ConstructionDashboardPage";
import ConstructionWorksPage from "./pages/construction/ConstructionWorksPage";
import ConstructionAddExpensePage from "./pages/construction/AddExpensePage";
import ConstructionMeasurementsPage from "./pages/construction/MeasurementsPage";
import ConstructionBudgetVsActualPage from "./pages/construction/BudgetVsActualPage";
import AgenciesPage from "./pages/agencies";
import ConstructionPage from "./pages/construction";
import ArchitectsEngineersPage from "./pages/architects-engineers";
import ConsultingPage from "./pages/consulting";
// Architects and Engineers
import ArchitectsEngineersDashboardPage from "./pages/architects-engineers/DashboardPage";
import ArchitectsEngineersProjectsPage from "./pages/architects-engineers/ProjectsPage";
import ArchitectsEngineersAddExpensePage from "./pages/architects-engineers/AddExpensePage";
import ArchitectsEngineersStagesPage from "./pages/architects-engineers/StagesPage";
import ArchitectsEngineersFeasibilityPage from "./pages/architects-engineers/FeasibilityPage";
// Consulting
import ConsultingDashboardPage from "./pages/consulting/DashboardPage";
import ConsultingProjectsPage from "./pages/consulting/ProjectsPage";
import ConsultingAddExpensePage from "./pages/consulting/AddExpensePage";
import ConsultingContractsPage from "./pages/consulting/ContractsPage";
import ConsultingProfitabilityPage from "./pages/consulting/ProfitabilityPage";
// Health Clinics
import HealthClinicsDashboardPage from "./pages/health-clinics/DashboardPage";
import HealthClinicsProceduresPage from "./pages/health-clinics/ProceduresPage";
import HealthClinicsRevenuePage from "./pages/health-clinics/RevenuePage";
// Law Firms
import LawFirmsDashboardPage from "./pages/law-firms/DashboardPage";
import LawFirmsCasesPage from "./pages/law-firms/CasesPage";
import LawFirmsFeesPage from "./pages/law-firms/FeesPage";
import LawFirmsPerformancePage from "./pages/law-firms/PerformancePage";
// Events
import EventsDashboardPage from "./pages/events/DashboardPage";
import EventsListPage from "./pages/events/ListPage";
import EventsAddExpensePage from "./pages/events/AddExpensePage";
import EventsReportPage from "./pages/events/ReportPage";
// IT Companies
import ITCompaniesDashboardPage from "./pages/it-companies/DashboardPage";
import ITCompaniesProjectsPage from "./pages/it-companies/ProjectsPage";
import ITCompaniesContractsPage from "./pages/it-companies/ContractsPage";
import ITCompaniesTicketsPage from "./pages/it-companies/TicketsPage";
import ITCompaniesSLAReportPage from "./pages/it-companies/SLAReportPage";
// Photographers
import PhotographersDashboardPage from "./pages/photographers/DashboardPage";
import PhotographersProjectsPage from "./pages/photographers/ProjectsPage";
import PhotographersAddExpensePage from "./pages/photographers/AddExpensePage";
import PhotographersEventsPage from "./pages/photographers/EventsPage";
import PhotographersProfitabilityPage from "./pages/photographers/ProfitabilityPage";
// Education
import EducationDashboardPage from "./pages/education/DashboardPage";
import EducationCoursesPage from "./pages/education/CoursesPage";
import EducationSalesPage from "./pages/education/SalesPage";
import EducationRevenuePage from "./pages/education/RevenuePage";
// Pilates/Yoga
import PilatesYogaDashboardPage from "./pages/pilates-yoga/DashboardPage";
import PilatesYogaSubscriptionsPage from "./pages/pilates-yoga/SubscriptionsPage";
import PilatesYogaClassesPage from "./pages/pilates-yoga/ClassesPage";
import PilatesYogaFinancialPage from "./pages/pilates-yoga/FinancialPage";
// Veterinary
import VeterinaryDashboardPage from "./pages/veterinary/DashboardPage";
import VeterinaryConsultationsPage from "./pages/veterinary/ConsultationsPage";
import VeterinaryProceduresPage from "./pages/veterinary/ProceduresPage";
import VeterinaryRevenuePage from "./pages/veterinary/RevenuePage";
// Beauty Clinics
import BeautyClinicsDashboardPage from "./pages/beauty-clinics/DashboardPage";
import BeautyClinicsProceduresPage from "./pages/beauty-clinics/ProceduresPage";
import BeautyClinicsPackagesPage from "./pages/beauty-clinics/PackagesPage";
import BeautyClinicsRevenuePage from "./pages/beauty-clinics/RevenuePage";
// Auto Repair
import AutoRepairDashboardPage from "./pages/auto-repair/DashboardPage";
import AutoRepairOrdersPage from "./pages/auto-repair/OrdersPage";
import AutoRepairVehiclesPage from "./pages/auto-repair/VehiclesPage";
import AutoRepairInventoryPage from "./pages/auto-repair/InventoryPage";
import AutoRepairServicesReportPage from "./pages/auto-repair/ServicesReportPage";
// Beauty Salons
import BeautySalonsDashboardPage from "./pages/beauty-salons/DashboardPage";
import BeautySalonsServicesPage from "./pages/beauty-salons/ServicesPage";
import BeautySalonsRevenuePage from "./pages/beauty-salons/RevenuePage";
import HealthClinicsPage from "./pages/health-clinics";
import LawFirmsPage from "./pages/law-firms";
import EventsPage from "./pages/events";
import ItCompaniesPage from "./pages/it-companies";
import PhotographersPage from "./pages/photographers";
import EducationPage from "./pages/education";
import PilatesYogaPage from "./pages/pilates-yoga";
import VeterinaryPage from "./pages/veterinary";
import BeautyClinicsPage from "./pages/beauty-clinics";
import AutoRepairPage from "./pages/auto-repair";
import BeautySalonsPage from "./pages/beauty-salons";

// Lazy load report hub pages
const CategoriesReportHub = lazy(() => import("./pages/reports/CategoriesReportHub"));
const CostCentersReportHub = lazy(() => import("./pages/reports/CostCentersReportHub"));
const PayablesReportHub = lazy(() => import("./pages/reports/PayablesReportHub"));
const ReceivablesReportHub = lazy(() => import("./pages/reports/ReceivablesReportHub"));
const CashFlowReportHub = lazy(() => import("./pages/reports/CashFlowReportHub"));

// Lazy load cash flow reports
const BankBalancesMonthly = lazy(() => import("./pages/reports/cash-flow/BankBalancesMonthly"));
const BankBalancesDaily = lazy(() => import("./pages/reports/cash-flow/BankBalancesDaily"));
const WeeklyDueDates = lazy(() => import("./pages/reports/cash-flow/WeeklyDueDates"));
const AveragePaymentReceiptPeriod = lazy(() => import("./pages/reports/cash-flow/AveragePaymentReceiptPeriod"));
const TransactionStatement = lazy(() => import("./pages/reports/cash-flow/TransactionStatement"));
const MonthlyCashFlow = lazy(() => import("./pages/reports/cash-flow/MonthlyCashFlow"));
const RevenueExpenseByDueDate = lazy(() => import("./pages/reports/cash-flow/RevenueExpenseByDueDate"));
const AveragePeriodChart = lazy(() => import("./pages/reports/cash-flow/AveragePeriodChart"));
const FinancialStatusSummary = lazy(() => import("./pages/reports/cash-flow/FinancialStatusSummary"));
const CashFlowAnalysis = lazy(() => import("./pages/reports/cash-flow/CashFlowAnalysis"));
const RevenueExpenseProjection = lazy(() => import("./pages/reports/cash-flow/RevenueExpenseProjection"));
const CashFlowByCategory = lazy(() => import("./pages/reports/cash-flow/CashFlowByCategory"));
const CashFlowByCostCenter = lazy(() => import("./pages/reports/cash-flow/CashFlowByCostCenter"));
const NetCashFlowTrend = lazy(() => import("./pages/reports/cash-flow/NetCashFlowTrend"));

// Lazy load category reports
const MonthlyCompetenceByCategory = lazy(() => import("./pages/reports/categories/MonthlyCompetenceByCategory"));
const TopCategoryCompetenceAnalysis = lazy(() => import("./pages/reports/categories/TopCategoryCompetenceAnalysis"));
const MonthlyCashByCategory = lazy(() => import("./pages/reports/categories/MonthlyCashByCategory"));
const TopExpensesByCategory = lazy(() => import("./pages/reports/categories/TopExpensesByCategory"));
const ExpenseDistribution = lazy(() => import("./pages/reports/categories/ExpenseDistribution"));
const RevenueDistribution = lazy(() => import("./pages/reports/categories/RevenueDistribution"));
const ExpenseCategoryStatus = lazy(() => import("./pages/reports/categories/ExpenseCategoryStatus"));
const RevenueCategoryStatus = lazy(() => import("./pages/reports/categories/RevenueCategoryStatus"));

// Lazy load cost center reports
const CostCenterMonthlyPayments = lazy(() => import("./pages/reports/cost-centers/CostCenterMonthlyPayments"));
const MonthlyBalanceByCostCenter = lazy(() => import("./pages/reports/cost-centers/MonthlyBalanceByCostCenter"));
const CostCenterStatus = lazy(() => import("./pages/reports/cost-centers/CostCenterStatus"));
const CostCenterCategoryBreakdown = lazy(() => import("./pages/reports/cost-centers/CostCenterCategoryBreakdown"));
const RealizedCashFlowByCostCenter = lazy(() => import("./pages/reports/cost-centers/RealizedCashFlowByCostCenter"));

// Lazy load payables reports
const SupplierPaymentsAnalysis = lazy(() => import("./pages/reports/payables/SupplierPaymentsAnalysis"));
const SupplierAnalysis = lazy(() => import("./pages/reports/payables/SupplierAnalysis"));
const TopSuppliersByExpense = lazy(() => import("./pages/reports/payables/TopSuppliersByExpense"));
const AveragePaymentPeriod = lazy(() => import("./pages/reports/payables/AveragePaymentPeriod"));
const PayablesList = lazy(() => import("./pages/reports/payables/PayablesList"));
const PaidPayablesList = lazy(() => import("./pages/reports/payables/PaidPayablesList"));
const PayablesDueList = lazy(() => import("./pages/reports/payables/PayablesDueList"));
const PurchaseFinancialStatus = lazy(() => import("./pages/reports/payables/PurchaseFinancialStatus"));
const SupplierFinancialStatus = lazy(() => import("./pages/reports/payables/SupplierFinancialStatus"));
const PayablesFinancialStatus = lazy(() => import("./pages/reports/payables/PayablesFinancialStatus"));
const OverduePayablesAnalysis = lazy(() => import("./pages/reports/payables/OverduePayablesAnalysis"));

// Lazy load receivables reports
const ReceivablesFinancialStatus = lazy(() => import("./pages/reports/receivables/ReceivablesFinancialStatus"));
const ClientDefaultRisk = lazy(() => import("./pages/reports/receivables/ClientDefaultRisk"));
const ReceivablesAgingReport = lazy(() => import("./pages/reports/receivables/ReceivablesAgingReport"));
const DefaultAnalysis = lazy(() => import("./pages/reports/receivables/DefaultAnalysis"));
const PaymentMethodDistribution = lazy(() => import("./pages/reports/receivables/PaymentMethodDistribution"));
const OverdueBucketAnalysis = lazy(() => import("./pages/reports/receivables/OverdueBucketAnalysis"));
import TopClientsByRevenue from "./pages/reports/receivables/TopClientsByRevenue";
const AverageReceiptPeriod = lazy(() => import("./pages/reports/receivables/AverageReceiptPeriod"));
const ClientBalanceByCompetence = lazy(() => import("./pages/reports/receivables/ClientBalanceByCompetence"));
const DueReceivablesList = lazy(() => import("./pages/reports/receivables/DueReceivablesList"));
const OpenReceivablesList = lazy(() => import("./pages/reports/receivables/OpenReceivablesList"));
const ReceivedRevenuesList = lazy(() => import("./pages/reports/receivables/ReceivedRevenuesList"));
const ClientReceiptsByBank = lazy(() => import("./pages/reports/receivables/ClientReceiptsByBank"));
const ClientFinancialStatus = lazy(() => import("./pages/reports/receivables/ClientFinancialStatus"));

// Custom Reports
import { CustomReportList } from "./pages/reports/custom/CustomReportList";
import { CustomReportTemplates } from "./pages/reports/custom/CustomReportTemplates";
import { CustomReportViewer } from "./pages/reports/custom/CustomReportViewer";
import { CustomReportBuilder } from "./pages/reports/custom/CustomReportBuilder";

// Loading component for lazy routes
const RouteLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-muted-foreground">Carregando...</p>
    </div>
  </div>
);

  // Inicializar TransactionDebugger em desenvolvimento
  if (process.env.NODE_ENV === "development") {
    TransactionDebugger.create();
  }

const App = () => (
  <ErrorBoundary
    context="App"
    severity="critical"
    enableRetry={true}
    maxRetries={2}
  >
    <NotificationProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        {process.env.NODE_ENV === "development" && <Agentation />}
        <BrowserRouter>
        <ThemeUpdateNotice />
      <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/login" element={<Login />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route
            path="/dashboard"
            element={
              <AuthGuard>
                <ErrorBoundary context="Dashboard" severity="high">
                  <DashboardPage />
                </ErrorBoundary>
              </AuthGuard>
            }
          />
          <Route
            path="/transactions/:type?"
            element={
              <AuthGuard>
                <ErrorBoundary context="Transactions" severity="high">
                  <Transactions />
                </ErrorBoundary>
              </AuthGuard>
            }
          />
          <Route
            path="/recurring"
            element={
              <AuthGuard>
                <ErrorBoundary context="Recurring Transactions" severity="high">
                  <RecurringPage />
                </ErrorBoundary>
              </AuthGuard>
            }
          />
          <Route
            path="/installments"
            element={
              <AuthGuard>
                <ErrorBoundary context="Installment Transactions" severity="high">
                  <InstallmentTransactionsPage />
                </ErrorBoundary>
              </AuthGuard>
            }
          />
            <Route
            path="/calendar"
            element={
              <AuthGuard>
                <CalendarPage />
              </AuthGuard>
            }
          />
          <Route
            path="/error-test"
            element={
              <AuthGuard>
                <ErrorTestPage />
              </AuthGuard>
            }
          />
          <Route
            path="/settings"
            element={
              <AuthGuard>
                <ErrorBoundary context="SettingsLayout" severity="medium">
                  <SettingsLayout />
                </ErrorBoundary>
              </AuthGuard>
            }
          >
            <Route index element={
              <AvatarErrorBoundary context="ProfileSettings" enableAvatarRefresh={true}>
                <ProfileSettings />
              </AvatarErrorBoundary>
            } />
            <Route path="empresa" element={<CompanySettings />} />
            <Route path="notifications" element={<NotificationsSettings />} />
            <Route path="security" element={<SecuritySettings />} />
            <Route path="users" element={<CompanyUsersPage />} />
            <Route path="delete-account" element={<DeleteAccount />} />
            
            {/* Rotas antigas para compatibilidade */}
            <Route path="profile" element={<Navigate to="/settings" replace />} />
            <Route path="company-users" element={<Navigate to="/settings/users" replace />} />
          </Route>
          
          {/* Admin Routes */}
          <Route
            path="/admin"
            element={<AdminLayout />}
          >
            <Route index element={<AdminDashboardPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="whatsapp" element={<WhatsAppPage />} />
            <Route path="color-palettes" element={<ColorPalettesPage />} />
            <Route path="feature-flags" element={<FeatureFlagsPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="subscriptions" element={<SubscriptionsPage />} />
            <Route path="feedbacks" element={<FeedbackDashboardPage />} />
          </Route>
          
          {/* Rotas de Cadastros - fora do settings */}
          <Route
            path="/cadastros/clients"
            element={
              <AuthGuard>
                <ClientsPage />
              </AuthGuard>
            }
          />
          <Route
            path="/cadastros/suppliers"
            element={
              <AuthGuard>
                <SuppliersPage />
              </AuthGuard>
            }
          />
          <Route
            path="/cadastros/cost-centers"
            element={
              <AuthGuard>
                <CostCentersPage />
              </AuthGuard>
            }
          />
          <Route
            path="/cadastros/categories"
            element={
              <AuthGuard>
                <CategoriesPage />
              </AuthGuard>
            }
          />
          <Route
            path="/cadastros/payment-accounts"
            element={
              <AuthGuard>
                <PaymentAccountsPage />
              </AuthGuard>
            }
          />
          
          {/* Services Routes */}
          <Route
            path="/services/budgets"
            element={
              <AuthGuard>
                <BudgetsLayout />
              </AuthGuard>
            }
          >
            <Route index element={<BudgetsListPage />} />
            <Route path="new" element={<BudgetFormPage />} />
            <Route path=":id" element={<BudgetViewPage />} />
            <Route path=":id/edit" element={<BudgetFormPage />} />
            <Route path=":id/duplicate" element={<BudgetDuplicatePage />} />
            <Route path="analytics" element={<BudgetAnalyticsPage />} />
            <Route path="reports" element={<BudgetReportsPage />} />
            <Route path="conversion" element={<BudgetConversionPage />} />
          </Route>
          <Route
            path="/services/sales"
            element={
              <AuthGuard>
                <ErrorBoundary context="ServicesSales" severity="high">
                  <SalesListPage />
                </ErrorBoundary>
              </AuthGuard>
            }
          />
          <Route
            path="/services/sales/new"
            element={
              <AuthGuard>
                <ErrorBoundary context="NewServicesSales" severity="high">
                  <SalesModalRoute />
                </ErrorBoundary>
              </AuthGuard>
            }
          />
          <Route
            path="/services/sales/:id/edit"
            element={
              <AuthGuard>
                <ErrorBoundary context="EditServicesSales" severity="high">
                  <SalesModalRoute />
                </ErrorBoundary>
              </AuthGuard>
            }
          />
          <Route
            path="/services/contracts"
            element={
              <AuthGuard>
                <ServicesContractsPage />
              </AuthGuard>
            }
          />
          <Route
            path="/services/work-orders"
            element={
              <AuthGuard>
                <WorkOrdersPage />
              </AuthGuard>
            }
          />
          <Route
            path="/services/installments"
            element={
              <AuthGuard>
                <InstallmentsPage />
              </AuthGuard>
            }
          />
          <Route
            path="/services/invoices"
            element={
              <AuthGuard>
                <ServicesInvoicesPage />
              </AuthGuard>
            }
          />
          <Route
            path="/services/serasa"
            element={
              <AuthGuard>
                <SerasaPage />
              </AuthGuard>
            }
          />
          <Route
            path="/services/due-reminders"
            element={
              <AuthGuard>
                <DueRemindersPage />
              </AuthGuard>
            }
          />
          <Route
            path="/services/cadastros/clients"
            element={
              <AuthGuard>
                <ServicesClientsPage />
              </AuthGuard>
            }
          />
          <Route
            path="/services/cadastros/services"
            element={
              <AuthGuard>
                <ServicesServicesPage />
              </AuthGuard>
            }
          />
          <Route
            path="/services/cadastros/carriers"
            element={
              <AuthGuard>
                <CarriersPage />
              </AuthGuard>
            }
          />
          <Route
            path="/services/settings/invoices"
            element={
              <AuthGuard>
                <InvoicesSettingsPage />
              </AuthGuard>
            }
          />
          <Route
            path="/services/settings/email-template"
            element={
              <AuthGuard>
                <EmailTemplatePage />
              </AuthGuard>
            }
          />
          <Route
            path="/services/settings/invoice-series"
            element={
              <AuthGuard>
                <InvoiceSeriesPage />
              </AuthGuard>
            }
          />
          
          <Route
            path="/reports"
            element={
              <AuthGuard>
                <ReportsPage />
              </AuthGuard>
            }
          />
          <Route
            path="/reports/categories"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <CategoriesReportHub />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/categories/monthly-competence-by-category"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <MonthlyCompetenceByCategory />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/categories/top-category-competence"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <TopCategoryCompetenceAnalysis />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/categories/monthly-cash-by-category"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <MonthlyCashByCategory />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/categories/top-expenses-by-category"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <TopExpensesByCategory />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/categories/expense-distribution"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <ExpenseDistribution />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/categories/revenue-distribution"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <RevenueDistribution />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/categories/expense-category-status"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <ExpenseCategoryStatus />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/categories/revenue-category-status"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <RevenueCategoryStatus />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/cost-centers"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <CostCentersReportHub />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/cost-centers/cost-center-monthly-payments"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <CostCenterMonthlyPayments />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/cost-centers/monthly-balance-by-cost-center"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <MonthlyBalanceByCostCenter />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/cost-centers/cost-center-status"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <CostCenterStatus />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/cost-centers/cost-center-category-breakdown"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <CostCenterCategoryBreakdown />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/cost-centers/realized-cash-flow-by-cost-center"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <RealizedCashFlowByCostCenter />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/payables"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <PayablesReportHub />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/payables/supplier-payments-analysis"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <SupplierPaymentsAnalysis />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/payables/top-suppliers-by-expense"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <TopSuppliersByExpense />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/payables/average-payment-period"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <AveragePaymentPeriod />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/payables/supplier-analysis"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <SupplierAnalysis />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/payables/payables-list"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <PayablesList />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/payables/paid-payables-list"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <PaidPayablesList />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/payables/payables-due-list"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <PayablesDueList />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/payables/purchase-financial-status"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <PurchaseFinancialStatus />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/payables/supplier-financial-status"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <SupplierFinancialStatus />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/payables/payables-financial-status"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <PayablesFinancialStatus />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/payables/overdue-payables-analysis"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <OverduePayablesAnalysis />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/receivables"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <ReceivablesReportHub />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/receivables/default-analysis"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <DefaultAnalysis />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/receivables/payment-method-distribution"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <PaymentMethodDistribution />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/receivables/overdue-bucket-analysis"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <OverdueBucketAnalysis />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/receivables/top-clients-by-revenue"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <TopClientsByRevenue />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/receivables/average-receipt-period"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <AverageReceiptPeriod />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/receivables/client-balance-by-competence"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <ClientBalanceByCompetence />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/receivables/due-receivables-list"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <DueReceivablesList />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/receivables/open-receivables-list"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <OpenReceivablesList />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/receivables/received-revenues-list"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <ReceivedRevenuesList />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/receivables/client-receipts-by-bank"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <ClientReceiptsByBank />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/receivables/client-financial-status"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <ClientFinancialStatus />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/receivables/receivables-financial-status"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <ReceivablesFinancialStatus />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/receivables/client-default-risk"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <ClientDefaultRisk />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/receivables/receivables-aging-report"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <ReceivablesAgingReport />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/cash-flow"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <CashFlowReportHub />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/cash-flow/bank-balances-monthly"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <BankBalancesMonthly />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/cash-flow/bank-balances-daily"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <BankBalancesDaily />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/cash-flow/weekly-due-dates"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <WeeklyDueDates />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/cash-flow/average-payment-receipt-period"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <AveragePaymentReceiptPeriod />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/cash-flow/transaction-statement"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <TransactionStatement />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/cash-flow/monthly-cash-flow"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <MonthlyCashFlow />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/cash-flow/revenue-expense-by-due-date"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <RevenueExpenseByDueDate />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/cash-flow/average-period-chart"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <AveragePeriodChart />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/cash-flow/financial-status-summary"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <FinancialStatusSummary />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/cash-flow/analysis"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <CashFlowAnalysis />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/cash-flow/revenue-expense-projection"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <RevenueExpenseProjection />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/cash-flow/by-category"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <CashFlowByCategory />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/cash-flow/by-cost-center"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <CashFlowByCostCenter />
                </Suspense>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/cash-flow/net-trend"
            element={
              <AuthGuard>
                <Suspense fallback={<RouteLoader />}>
                  <NetCashFlowTrend />
                </Suspense>
              </AuthGuard>
            }
          />
          {/* Custom Reports Routes */}
          <Route
            path="/reports/custom"
            element={
              <AuthGuard>
                <ErrorBoundary context="CustomReportsList" severity="high">
                  <CustomReportList />
                </ErrorBoundary>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/custom/templates"
            element={
              <AuthGuard>
                <ErrorBoundary context="CustomReportTemplates" severity="high">
                  <CustomReportTemplates />
                </ErrorBoundary>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/custom/new"
            element={
              <AuthGuard>
                <ErrorBoundary context="CustomReportBuilder" severity="high">
                  <CustomReportBuilder mode="create" />
                </ErrorBoundary>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/custom/:id"
            element={
              <AuthGuard>
                <ErrorBoundary context="CustomReportViewer" severity="high">
                  <CustomReportViewer />
                </ErrorBoundary>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/custom/:id/edit"
            element={
              <AuthGuard>
                <ErrorBoundary context="CustomReportBuilder" severity="high">
                  <CustomReportBuilder mode="edit" />
                </ErrorBoundary>
              </AuthGuard>
            }
          />
          <Route
            path="/reconciliation"
            element={
              <AuthGuard>
                <Reconciliation />
              </AuthGuard>
            }
          />
          <Route
            path="/cash-flow"
            element={
              <AuthGuard>
                <CashFlow />
              </AuthGuard>
            }
          />
          <Route
            path="/overdue"
            element={
              <AuthGuard>
                <Overdue />
              </AuthGuard>
            }
          />
          <Route
            path="/accrual"
            element={
              <AuthGuard>
                <Accrual />
              </AuthGuard>
            }
          />
          {/* Project Management Routes (for Agencies) */}
          <Route
            path="/projects/dashboard"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.AGENCIES}>
                  <ProjectDashboardPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/projects/list"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.AGENCIES}>
                  <ProjectsListPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/projects/add-expense"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.AGENCIES}>
                  <AddExpensePage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/projects/contracts"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.AGENCIES}>
                  <ContractsPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/projects/profitability"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.AGENCIES}>
                  <ProfitabilityPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Construction Routes (for Construction Companies) */}
          <Route
            path="/construction/dashboard"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.CONSTRUCTION}>
                  <ConstructionDashboardPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/construction/works"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.CONSTRUCTION}>
                  <ConstructionWorksPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/construction/add-expense"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.CONSTRUCTION}>
                  <ConstructionAddExpensePage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/construction/measurements"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.CONSTRUCTION}>
                  <ConstructionMeasurementsPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/construction/budget-vs-actual"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.CONSTRUCTION}>
                  <ConstructionBudgetVsActualPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Architects and Engineers Routes */}
          <Route
            path="/architects-engineers/dashboard"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.ARCHITECTS_ENGINEERS}>
                  <ArchitectsEngineersDashboardPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/architects-engineers/projects"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.ARCHITECTS_ENGINEERS}>
                  <ArchitectsEngineersProjectsPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/architects-engineers/add-expense"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.ARCHITECTS_ENGINEERS}>
                  <ArchitectsEngineersAddExpensePage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/architects-engineers/stages"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.ARCHITECTS_ENGINEERS}>
                  <ArchitectsEngineersStagesPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/architects-engineers/feasibility"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.ARCHITECTS_ENGINEERS}>
                  <ArchitectsEngineersFeasibilityPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Consulting Routes */}
          <Route
            path="/consulting/dashboard"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.CONSULTING_B2B}>
                  <ConsultingDashboardPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/consulting/projects"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.CONSULTING_B2B}>
                  <ConsultingProjectsPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/consulting/add-expense"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.CONSULTING_B2B}>
                  <ConsultingAddExpensePage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/consulting/contracts"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.CONSULTING_B2B}>
                  <ConsultingContractsPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/consulting/profitability"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.CONSULTING_B2B}>
                  <ConsultingProfitabilityPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Agencies Routes */}
          <Route
            path="/agencies"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.AGENCIES}>
                  <AgenciesPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Construction Routes */}
          <Route
            path="/construction"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.CONSTRUCTION}>
                  <ConstructionPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Architects and Engineers Routes */}
          <Route
            path="/architects-engineers"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.ARCHITECTS_ENGINEERS}>
                  <ArchitectsEngineersPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Consulting Routes */}
          <Route
            path="/consulting"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.CONSULTING_B2B}>
                  <ConsultingPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Health Clinics Routes */}
          <Route
            path="/health-clinics/dashboard"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.HEALTH_CLINICS}>
                  <HealthClinicsDashboardPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/health-clinics/procedures"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.HEALTH_CLINICS}>
                  <HealthClinicsProceduresPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/health-clinics/revenue"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.HEALTH_CLINICS}>
                  <HealthClinicsRevenuePage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Law Firms Routes */}
          <Route
            path="/law-firms/dashboard"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.LAW_FIRMS}>
                  <LawFirmsDashboardPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/law-firms/cases"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.LAW_FIRMS}>
                  <LawFirmsCasesPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/law-firms/fees"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.LAW_FIRMS}>
                  <LawFirmsFeesPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/law-firms/performance"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.LAW_FIRMS}>
                  <LawFirmsPerformancePage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Events Routes */}
          <Route
            path="/events/dashboard"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.EVENT_PRODUCERS}>
                  <EventsDashboardPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/events/list"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.EVENT_PRODUCERS}>
                  <EventsListPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/events/add-expense"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.EVENT_PRODUCERS}>
                  <EventsAddExpensePage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/events/report"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.EVENT_PRODUCERS}>
                  <EventsReportPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* IT Companies Routes */}
          <Route
            path="/it-companies/dashboard"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.IT_COMPANIES}>
                  <ITCompaniesDashboardPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/it-companies/projects"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.IT_COMPANIES}>
                  <ITCompaniesProjectsPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/it-companies/contracts"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.IT_COMPANIES}>
                  <ITCompaniesContractsPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/it-companies/tickets"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.IT_COMPANIES}>
                  <ITCompaniesTicketsPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/it-companies/sla-report"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.IT_COMPANIES}>
                  <ITCompaniesSLAReportPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Photographers Routes */}
          <Route
            path="/photographers/dashboard"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.PHOTOGRAPHERS_VIDEOGRAPHERS}>
                  <PhotographersDashboardPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/photographers/projects"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.PHOTOGRAPHERS_VIDEOGRAPHERS}>
                  <PhotographersProjectsPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/photographers/add-expense"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.PHOTOGRAPHERS_VIDEOGRAPHERS}>
                  <PhotographersAddExpensePage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/photographers/events"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.PHOTOGRAPHERS_VIDEOGRAPHERS}>
                  <PhotographersEventsPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/photographers/profitability"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.PHOTOGRAPHERS_VIDEOGRAPHERS}>
                  <PhotographersProfitabilityPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Education Routes */}
          <Route
            path="/education/dashboard"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.EDUCATION_PROFESSIONALS}>
                  <EducationDashboardPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/education/courses"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.EDUCATION_PROFESSIONALS}>
                  <EducationCoursesPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/education/sales"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.EDUCATION_PROFESSIONALS}>
                  <EducationSalesPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/education/revenue"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.EDUCATION_PROFESSIONALS}>
                  <EducationRevenuePage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Pilates/Yoga Routes */}
          <Route
            path="/pilates-yoga/dashboard"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.PILATES_YOGA_STUDIOS}>
                  <PilatesYogaDashboardPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/pilates-yoga/subscriptions"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.PILATES_YOGA_STUDIOS}>
                  <PilatesYogaSubscriptionsPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/pilates-yoga/classes"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.PILATES_YOGA_STUDIOS}>
                  <PilatesYogaClassesPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/pilates-yoga/financial"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.PILATES_YOGA_STUDIOS}>
                  <PilatesYogaFinancialPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Veterinary Routes */}
          <Route
            path="/veterinary/dashboard"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.VETERINARY_CLINICS}>
                  <VeterinaryDashboardPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/veterinary/consultations"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.VETERINARY_CLINICS}>
                  <VeterinaryConsultationsPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/veterinary/procedures"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.VETERINARY_CLINICS}>
                  <VeterinaryProceduresPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/veterinary/revenue"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.VETERINARY_CLINICS}>
                  <VeterinaryRevenuePage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Beauty Clinics Routes */}
          <Route
            path="/beauty-clinics/dashboard"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.BEAUTY_CLINICS}>
                  <BeautyClinicsDashboardPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/beauty-clinics/procedures"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.BEAUTY_CLINICS}>
                  <BeautyClinicsProceduresPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/beauty-clinics/packages"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.BEAUTY_CLINICS}>
                  <BeautyClinicsPackagesPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/beauty-clinics/revenue"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.BEAUTY_CLINICS}>
                  <BeautyClinicsRevenuePage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Auto Repair Routes */}
          <Route
            path="/auto-repair/dashboard"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.AUTO_REPAIR_SHOPS}>
                  <AutoRepairDashboardPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/auto-repair/orders"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.AUTO_REPAIR_SHOPS}>
                  <AutoRepairOrdersPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/auto-repair/vehicles"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.AUTO_REPAIR_SHOPS}>
                  <AutoRepairVehiclesPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/auto-repair/inventory"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.AUTO_REPAIR_SHOPS}>
                  <AutoRepairInventoryPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/auto-repair/services-report"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.AUTO_REPAIR_SHOPS}>
                  <AutoRepairServicesReportPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Beauty Salons Routes */}
          <Route
            path="/beauty-salons/dashboard"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.BEAUTY_SALONS}>
                  <BeautySalonsDashboardPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/beauty-salons/services"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.BEAUTY_SALONS}>
                  <BeautySalonsServicesPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/beauty-salons/revenue"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.BEAUTY_SALONS}>
                  <BeautySalonsRevenuePage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Health Clinics Routes */}
          <Route
            path="/health-clinics"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.HEALTH_CLINICS}>
                  <HealthClinicsPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Law Firms Routes */}
          <Route
            path="/law-firms"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.LAW_FIRMS}>
                  <LawFirmsPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Events Routes */}
          <Route
            path="/events"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.EVENT_PRODUCERS}>
                  <EventsPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* IT Companies Routes */}
          <Route
            path="/it-companies"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.IT_COMPANIES}>
                  <ItCompaniesPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Photographers Routes */}
          <Route
            path="/photographers"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.PHOTOGRAPHERS_VIDEOGRAPHERS}>
                  <PhotographersPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Education Routes */}
          <Route
            path="/education"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.EDUCATION_PROFESSIONALS}>
                  <EducationPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Pilates/Yoga Routes */}
          <Route
            path="/pilates-yoga"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.PILATES_YOGA_STUDIOS}>
                  <PilatesYogaPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Veterinary Routes */}
          <Route
            path="/veterinary"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.VETERINARY_CLINICS}>
                  <VeterinaryPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Beauty Clinics Routes */}
          <Route
            path="/beauty-clinics"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.BEAUTY_CLINICS}>
                  <BeautyClinicsPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Auto Repair Routes */}
          <Route
            path="/auto-repair"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.AUTO_REPAIR_SHOPS}>
                  <AutoRepairPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          {/* Beauty Salons Routes */}
          <Route
            path="/beauty-salons"
            element={
              <AuthGuard>
                <IndustryRouteGuard requiredDomain={CompanyType.BEAUTY_SALONS}>
                  <BeautySalonsPage />
                </IndustryRouteGuard>
              </AuthGuard>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
      </TooltipProvider>
    </NotificationProvider>
  </ErrorBoundary>
);

export default App;
